import {
  createUserSchema,
  updateUserSchema,
  type CreateUserSchema,
} from '@/api/users/user.schema';
import { ContentLoader } from '@/components/ui/loaders';
import { Roles } from '@/lib/selectors/roles';
import { useCreateUser, useDeleteUser, useUpdateUser, useUser } from '@/services/users';
import { zodResolver } from '@hookform/resolvers/zod';
import { Role } from 'generated/prisma/enums';
import { useEffect } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdminFormPage, useServerErrors } from './admin-form';
import { ChoiceGroup, FormSection, TextField, type ChoiceOption } from './fields';
import { ACTIVE_OPTIONS } from './sacraments';

const LIST = '/admin/users';

const ROLE_OPTIONS: ChoiceOption<Role>[] = [
  { value: Role.ADMIN, label: Roles[Role.ADMIN] },
  { value: Role.USER, label: Roles[Role.USER] },
];

interface UserFormValues {
  name: string;
  email: string;
  password: string;
  role: Role;
  isActive: boolean;
}

const EMPTY: UserFormValues = {
  name: '',
  email: '',
  password: '',
  role: Role.USER,
  isActive: true,
};

export function UserForm({ id }: { id?: string }) {
  const navigate = useNavigate();
  const { data: user, isLoading } = useUser(id ?? '');
  const create = useCreateUser();
  const update = useUpdateUser();
  const remove = useDeleteUser();
  const saving = create.isPending || update.isPending;

  const form = useForm<UserFormValues>({
    resolver: zodResolver(
      id ? updateUserSchema : createUserSchema,
    ) as unknown as Resolver<UserFormValues>,
    defaultValues: EMPTY,
  });
  const { errors } = form.formState;

  useServerErrors(form, create.error ?? update.error);

  useEffect(() => {
    if (!user) return;
    form.reset({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      isActive: user.isActive,
    });
  }, [user]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const data = values as unknown as CreateUserSchema;
    try {
      if (id) await update.mutateAsync({ id, data });
      else await create.mutateAsync(data);
      navigate(LIST);
    } catch {
      // erros exibidos via toast / useServerErrors
    }
  });

  const handleDelete = async () => {
    if (!id) return;
    try {
      await remove.mutateAsync(id);
      navigate(LIST);
    } catch {
      // toast de erro exibido pelo hook
    }
  };

  return (
    <>
      <ContentLoader show={(!!id && isLoading) || saving || remove.isPending} />
      <AdminFormPage
        backTo={LIST}
        backLabel="Voltar para Usuários"
        eyebrow="USUÁRIOS"
        title={id ? 'Editar usuário' : 'Novo usuário'}
        description="Quem pode acessar o painel e com qual perfil."
        onSubmit={handleSubmit}
        onCancel={() => navigate(LIST)}
        saveLabel={id ? 'Salvar alterações' : 'Criar cadastro'}
        saving={saving}
        onDelete={id ? handleDelete : undefined}
      >
        <FormSection
          eyebrow="DADOS DE ACESSO"
          title="Informações do usuário"
          className="rounded-[18px]"
        >
          <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            <TextField
              wrapperClassName="col-span-full"
              label="Nome completo *"
              placeholder="Ex: Adriana Oliveira"
              error={errors.name?.message}
              {...form.register('name')}
            />
            <TextField
              label="E-mail *"
              type="email"
              placeholder="nome@casaisencheivos.com.br"
              error={errors.email?.message}
              {...form.register('email')}
            />
            <TextField
              label={id ? 'Senha' : 'Senha *'}
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo de 8 caracteres"
              hint={id ? 'Deixe em branco para manter a senha atual.' : undefined}
              error={errors.password?.message}
              {...form.register('password')}
            />
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <ChoiceGroup
                  className="col-span-full"
                  label="Perfil de acesso"
                  options={ROLE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <ChoiceGroup
                  className="col-span-full"
                  label="Status"
                  options={ACTIVE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </FormSection>
      </AdminFormPage>
    </>
  );
}
